import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { TierIiReportStep } from "generated-graphql/graphql";
import { useRYTag, useTierIITag } from "hooks/useDocumentTags";
import { DocumentsGrid } from "routes/Customer/Documents/DocumentsGrid";
import { reportStepMetadata } from "util/reportStepMetadata";
import { ReportFloatingBar } from "../ReportFloatingBar";
import { StepperPageHeader } from "../StepperPageHeader";
import { useHmbpFeature } from "../useHmbp";
import {
  GET_REPORT,
  useAllDocumentIssues,
  useReport,
  useReportFacilityDocumentIssues,
} from "../useReport";
import { useReportIsVerified } from "../useReportIsVerified";
import { useTouchReportMutation } from "../useTouchReportMutation";

const TIER_II_REPORT_SUBMITTED_DOCUMENTS = gql(`
  query TierIIReportSubmittedDocuments($reportId: ID!) {
    tierIIReportSubmittedDocuments(reportId: $reportId) {
      ...DocumentGridRow
    }
  }
`);

export const Documents = () => {
  const { data: reportData } = useReport();
  const {
    data: documentData,
    previousData: previousDocumentData,
    loading: documentLoading,
  } = useQuery(TIER_II_REPORT_SUBMITTED_DOCUMENTS, {
    variables: {
      reportId: reportData?.tierIIReport.id ?? "",
    },
    skip: !reportData?.tierIIReport.id,
    fetchPolicy: "cache-and-network",
  });

  const isVerified = useReportIsVerified();
  const isHmbp = useHmbpFeature(reportData?.tierIIReport.facility.state ?? "");
  const { handleSave, loading: touchLoading } = useTouchReportMutation(
    reportData?.tierIIReport.id ?? "",
    reportData?.tierIIReport.touchedSteps ?? [],
    TierIiReportStep.Documents
  );
  const issues = useAllDocumentIssues();
  const facilityDocIssues = useReportFacilityDocumentIssues();

  const reportingYearTag = useRYTag(reportData?.tierIIReport.reportingYear);
  const tierIITag = useTierIITag();

  const showInitialLoading = documentLoading && !previousDocumentData;
  return (
    <>
      <StepperPageHeader
        header={reportStepMetadata.DOCUMENTS.title}
        description={reportStepMetadata.DOCUMENTS.getOverviewDescription({
          isHmbp,
        })}
        mb={0}
      />
      <DocumentsGrid
        loading={showInitialLoading}
        tenantId={reportData?.tierIIReport.tenantId}
        documents={
          documentData?.tierIIReportSubmittedDocuments ??
          previousDocumentData?.tierIIReportSubmittedDocuments ??
          []
        }
        facilityDocumentIssues={facilityDocIssues}
        facility={
          reportData?.tierIIReport.facility
            ? {
                id: reportData.tierIIReport.facility.id,
                name: reportData.tierIIReport.facility.name,
              }
            : undefined
        }
        reportId={reportData?.tierIIReport.id}
        refetchQueries={[GET_REPORT, TIER_II_REPORT_SUBMITTED_DOCUMENTS]}
        tags={[tierIITag, reportingYearTag]}
        deleteDocument={false}
        isReadOnly={isVerified}
        addEditDocumentTypeStrategy="tier-ii"
      />
      {!isVerified && (
        <ReportFloatingBar
          loading={touchLoading}
          onSaveClick={handleSave}
          issues={issues}
          saveDisabled={issues.length > 0}
          saveText="Mark as Complete"
        />
      )}
    </>
  );
};
